import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  Chip,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff, Save as SaveIcon, SmartToy as AIIcon } from '@mui/icons-material';
import { auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getOrCreateUserProfile } from '../../utils/userService';
import {
  getAgentConfig,
  saveAgentConfig,
  defaultCustomerSupportConfig,
  defaultReportGeneratorConfig,
} from '../../utils/aiAgentService';

function AIAgents() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Customer Support Agent
  const [supportConfig, setSupportConfig] = useState(defaultCustomerSupportConfig);
  const [showSupportKey, setShowSupportKey] = useState(false);
  
  // Report Generator Agent (future)
  const [reportConfig, setReportConfig] = useState(defaultReportGeneratorConfig);
  const [showReportKey, setShowReportKey] = useState(false);
  
  const [saveStatus, setSaveStatus] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        const profile = await getOrCreateUserProfile(user);
        setUserProfile(profile);
        
        if (profile.role !== 'admin') {
          navigate('/');
          return;
        }
        
        await loadConfigs();
      } else {
        navigate('/');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const loadConfigs = async () => {
    setLoading(true);
    try {
      // Load customer support agent config
      const support = await getAgentConfig('customer-support');
      if (support) {
        setSupportConfig(support);
      }
      
      // Load report generator agent config
      const report = await getAgentConfig('report-generator');
      if (report) {
        setReportConfig(report);
      }
    } catch (error) {
      console.error('Error loading configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSupportConfig = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      const success = await saveAgentConfig('customer-support', supportConfig);
      if (success) {
        setSaveStatus({ type: 'success', message: '✅ Customer Support Agent configuration saved!' });
      } else {
        setSaveStatus({ type: 'error', message: '❌ Failed to save configuration' });
      }
    } catch (error) {
      setSaveStatus({ type: 'error', message: '❌ Error: ' + error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveReportConfig = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      const success = await saveAgentConfig('report-generator', reportConfig);
      if (success) {
        setSaveStatus({ type: 'success', message: '✅ Report Generator Agent configuration saved!' });
      } else {
        setSaveStatus({ type: 'error', message: '❌ Failed to save configuration' });
      }
    } catch (error) {
      setSaveStatus({ type: 'error', message: '❌ Error: ' + error.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
        <Typography sx={{ color: 'text.primary' }}>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '2px solid',
          borderColor: 'divider',
          py: 4,
          px: 3,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  background: 'linear-gradient(90deg, #00FFF5 0%, #9D00FF 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                🤖 AI Agents
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                Configure AI models and agents for automated assistance
              </Typography>
            </Box>
            <Button onClick={() => navigate('/admin')} variant="outlined">
              ← Back to Dashboard
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {saveStatus && (
          <Alert severity={saveStatus.type} sx={{ mb: 3 }} onClose={() => setSaveStatus(null)}>
            {saveStatus.message}
          </Alert>
        )}

        {/* Customer Support Agent */}
        <Paper sx={{ p: 4, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <AIIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 600, mb: 0.5 }}>
                Customer Support Agent
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                AI-powered agent for handling customer inquiries, refunds, and support tickets
              </Typography>
            </Box>
            <Chip
              label={supportConfig.enabled ? 'Active' : 'Inactive'}
              color={supportConfig.enabled ? 'success' : 'default'}
              sx={{ fontWeight: 600 }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'grid', gap: 3 }}>
            {/* Model Selection */}
            <FormControl fullWidth>
              <InputLabel>Model</InputLabel>
              <Select
                value={supportConfig.model || 'gpt-4o'}
                onChange={(e) => setSupportConfig({ ...supportConfig, model: e.target.value })}
                label="Model"
              >
                {/* OpenAI Models */}
                <MenuItem value="gpt-4o">GPT-4o (Recommended)</MenuItem>
                <MenuItem value="gpt-4o-mini">GPT-4o Mini (Fast)</MenuItem>
                <MenuItem value="gpt-4-turbo">GPT-4 Turbo</MenuItem>
                
                {/* Anthropic Claude 4.5 Models (Latest) */}
                <MenuItem value="claude-sonnet-4-5-20250929">Claude Sonnet 4.5 (Latest)</MenuItem>
                <MenuItem value="claude-opus-4-5-20251101">Claude Opus 4.5 (Most Capable)</MenuItem>
                <MenuItem value="claude-haiku-4-5-20251001">Claude Haiku 4.5 (Fast)</MenuItem>
                
                {/* Anthropic Claude 4 Models */}
                <MenuItem value="claude-opus-4-1-20250805">Claude Opus 4.1</MenuItem>
                <MenuItem value="claude-sonnet-4-20250514">Claude Sonnet 4</MenuItem>
                <MenuItem value="claude-opus-4-20250514">Claude Opus 4</MenuItem>
                
                {/* Anthropic Claude 3.5/3 Models */}
                <MenuItem value="claude-3-5-haiku-20241022">Claude Haiku 3.5</MenuItem>
                <MenuItem value="claude-3-haiku-20240307">Claude Haiku 3 (Fastest)</MenuItem>
              </Select>
            </FormControl>

            {/* API Keys - Both stored, appropriate one used based on model */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="OpenAI API Key"
                type={showSupportKey ? 'text' : 'password'}
                value={supportConfig.openaiApiKey || ''}
                onChange={(e) => setSupportConfig({ ...supportConfig, openaiApiKey: e.target.value })}
                fullWidth
                placeholder="sk-..."
                helperText={supportConfig.model?.includes('claude') ? '' : '✓ Active'}
                sx={{
                  opacity: supportConfig.model?.includes('claude') ? 0.6 : 1,
                  '& .MuiFormHelperText-root': { color: '#22c55e' }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowSupportKey(!showSupportKey)} edge="end" size="small">
                        {showSupportKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Anthropic API Key"
                type={showSupportKey ? 'text' : 'password'}
                value={supportConfig.anthropicApiKey || ''}
                onChange={(e) => setSupportConfig({ ...supportConfig, anthropicApiKey: e.target.value })}
                fullWidth
                placeholder="sk-ant-..."
                helperText={supportConfig.model?.includes('claude') ? '✓ Active' : ''}
                sx={{
                  opacity: supportConfig.model?.includes('claude') ? 1 : 0.6,
                  '& .MuiFormHelperText-root': { color: '#22c55e' }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowSupportKey(!showSupportKey)} edge="end" size="small">
                        {showSupportKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* System Prompt */}
            <TextField
              label="System Prompt"
              multiline
              rows={12}
              value={supportConfig.systemPrompt || ''}
              onChange={(e) => setSupportConfig({ ...supportConfig, systemPrompt: e.target.value })}
              fullWidth
              helperText="Instructions for how the AI should behave and respond to customers"
            />

            {/* Advanced Settings */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Temperature"
                type="number"
                inputProps={{ min: 0, max: 2, step: 0.1 }}
                value={supportConfig.temperature || 0.7}
                onChange={(e) => setSupportConfig({ ...supportConfig, temperature: parseFloat(e.target.value) })}
                helperText="0 = Deterministic, 2 = Creative"
              />
              <TextField
                label="Max Tokens"
                type="number"
                inputProps={{ min: 100, max: 2000, step: 50 }}
                value={supportConfig.maxTokens || 500}
                onChange={(e) => setSupportConfig({ ...supportConfig, maxTokens: parseInt(e.target.value) })}
                helperText="Maximum response length"
              />
            </Box>

            {/* Auto-Respond Toggle */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={supportConfig.autoRespond || false}
                    onChange={(e) => setSupportConfig({ ...supportConfig, autoRespond: e.target.checked })}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>Auto-Respond to Customer Messages</Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                      ⚠️ When enabled, AI will automatically reply to all customer messages without human review.
                      Enable only after thorough testing.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {/* Enabled Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={supportConfig.enabled !== false}
                  onChange={(e) => setSupportConfig({ ...supportConfig, enabled: e.target.checked })}
                  color="success"
                />
              }
              label="Agent Enabled"
            />

            {/* Save Button */}
            <Button
              variant="contained"
              size="large"
              startIcon={<SaveIcon />}
              onClick={handleSaveSupportConfig}
              disabled={saving || !supportConfig.apiKey}
              fullWidth
            >
              {saving ? 'Saving...' : 'Save Customer Support Agent'}
            </Button>
          </Box>
        </Paper>

        {/* Report Generator Agent (Future) */}
        <Paper sx={{ p: 4, opacity: 0.6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <AIIcon sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 600, mb: 0.5 }}>
                Report Generator Agent
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                AI-powered agent for analyzing user data and generating forecast reports
              </Typography>
            </Box>
            <Chip label="Coming Soon" color="default" sx={{ fontWeight: 600 }} />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Alert severity="info">
            <Typography sx={{ fontWeight: 600, mb: 1 }}>This agent is not yet available</Typography>
            <Typography variant="body2">
              The Report Generator Agent will analyze user chat logs and structured data to create comprehensive 
              PatternLock Forecast reports. Configuration will be available in a future update.
            </Typography>
          </Alert>
        </Paper>

        {/* Info Box */}
        <Paper sx={{ p: 3, mt: 4, bgcolor: 'rgba(0,255,245,0.05)', border: '1px solid rgba(0,255,245,0.2)' }}>
          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
            💡 How AI Agents Work
          </Typography>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box>
              <Typography sx={{ color: 'text.primary', fontWeight: 600, mb: 0.5 }}>
                1. Customer Support Agent
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                - Reviews conversation history when you click "AI Assist" in admin inbox
                - Generates contextual responses based on the system prompt
                - Suggests actions (help, credit, refund) based on message type
                - Maintains conversation context across multiple messages
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ color: 'text.primary', fontWeight: 600, mb: 0.5 }}>
                2. Auto-Respond (Optional)
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                - When enabled, AI automatically responds to all new messages
                - Bypasses human review (use with caution!)
                - Best enabled after extensive testing and validation
                - Can be toggled on/off at any time
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ color: 'text.primary', fontWeight: 600, mb: 0.5 }}>
                3. Security & Best Practices
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                - API keys are stored securely in Firestore (admin access only)
                - System prompts define agent behavior and constraints
                - Temperature controls response creativity (lower = more consistent)
                - Max tokens limits response length and API costs
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default AIAgents;

